import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, Users, GraduationCap, BookOpen, User, Phone, Mail, Facebook, Cake, Filter } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DirectorioPage() {
  const [directorio, setDirectorio] = useState([]);
  const [filteredDirectorio, setFilteredDirectorio] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterArea, setFilterArea] = useState("");

  const fetchDirectorio = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/directorio`);
      setDirectorio(res.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching directorio:", error);
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/directorio/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  const filterData = useCallback(() => {
    let filtered = [...directorio];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => 
        entry.nombre?.toLowerCase().includes(term) ||
        entry.apodo?.toLowerCase().includes(term) ||
        entry.area?.toLowerCase().includes(term)
      );
    }
    
    if (filterTipo) {
      filtered = filtered.filter(entry => entry.tipo === filterTipo);
    }
    
    if (filterArea) {
      filtered = filtered.filter(entry => entry.area === filterArea);
    }
    
    setFilteredDirectorio(filtered);
  }, [directorio, searchTerm, filterTipo, filterArea]);

  useEffect(() => {
    fetchDirectorio();
    fetchStats();
  }, [fetchDirectorio, fetchStats]);

  useEffect(() => {
    filterData();
  }, [filterData]);

  const getTipoIcon = (tipo) => {
    if (tipo === "Exalumno 93-96") return <GraduationCap className="h-4 w-4" />;
    if (tipo === "Maestro") return <BookOpen className="h-4 w-4" />;
    return <User className="h-4 w-4" />;
  };

  const getTipoColor = (tipo) => {
    if (tipo === "Exalumno 93-96") return "bg-zuno-gold/20 text-zuno-gold border-zuno-gold/30";
    if (tipo === "Maestro") return "bg-blue-100 text-blue-700 border-blue-300";
    return "bg-slate-100 text-slate-600 border-slate-300";
  };

  const areas = ["Medicina", "Humanidades", "Arquitectura", "Ingenieria", "Administracion"];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zuno-wine"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-zuno-wine text-white py-6">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors">
            <ArrowLeft size={20} />
            <span>Volver al inicio</span>
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold">Directorio de la Generación</h1>
          <p className="text-white/80 mt-2">Prepa Zuno 93-96 - Exalumnos, Maestros e Invitados</p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="bg-zuno-wine/10 p-2 rounded-lg">
                  <Users className="h-5 w-5 text-zuno-wine" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                  <p className="text-sm text-slate-500">Total</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="bg-zuno-gold/10 p-2 rounded-lg">
                  <GraduationCap className="h-5 w-5 text-zuno-gold" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.exalumnos}</p>
                  <p className="text-sm text-slate-500">Exalumnos</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.maestros}</p>
                  <p className="text-sm text-slate-500">Maestros</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="bg-slate-100 p-2 rounded-lg">
                  <User className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.invitados}</p>
                  <p className="text-sm text-slate-500">Invitados</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Buscar por nombre o apodo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && filterData()}
                  className="pl-10"
                  data-testid="search-directorio"
                />
              </div>
              <Button 
                onClick={filterData}
                className="bg-zuno-wine hover:bg-zuno-wine/90"
                data-testid="search-button"
              >
                <Search className="h-4 w-4 mr-2" />
                Buscar
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              <select
                value={filterTipo}
                onChange={(e) => setFilterTipo(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="filter-tipo"
              >
                <option value="">Todos los tipos</option>
                <option value="Exalumno 93-96">Exalumnos</option>
                <option value="Maestro">Maestros</option>
                <option value="Invitado no Graduado">Invitados no Graduados</option>
                <option value="Invitado Otra generacion">Otra Generación</option>
              </select>
              <select
                value={filterArea}
                onChange={(e) => setFilterArea(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="filter-area"
              >
                <option value="">Todas las áreas</option>
                {areas.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
              {(filterTipo || filterArea || searchTerm) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFilterTipo("");
                    setFilterArea("");
                    setSearchTerm("");
                  }}
                >
                  Limpiar filtros
                </Button>
              )}
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-3">
            Mostrando {filteredDirectorio.length} de {directorio.length} registros
          </p>
        </div>

        {/* Directory List */}
        <div className="grid gap-3">
          {filteredDirectorio.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center">
              <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No se encontraron registros</p>
            </div>
          ) : (
            filteredDirectorio.map((entry) => (
              <div
                key={entry.id}
                className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow"
                data-testid={`directorio-entry-${entry.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  {/* Name and Badge */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-800">{entry.nombre}</h3>
                      {entry.apodo && (
                        <span className="text-sm text-slate-500">"{entry.apodo}"</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${getTipoColor(entry.tipo)}`}>
                        {getTipoIcon(entry.tipo)}
                        {entry.tipo}
                      </span>
                      {entry.area && (
                        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                          {entry.area}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="flex flex-wrap gap-3 text-sm">
                    {entry.telefono && (
                      <a
                        href={`tel:${entry.telefono}`}
                        className="inline-flex items-center gap-1 text-slate-600 hover:text-zuno-wine"
                      >
                        <Phone className="h-4 w-4" />
                        <span className="hidden md:inline">{entry.telefono}</span>
                      </a>
                    )}
                    {entry.email && (
                      <a
                        href={`mailto:${entry.email}`}
                        className="inline-flex items-center gap-1 text-slate-600 hover:text-zuno-wine"
                      >
                        <Mail className="h-4 w-4" />
                      </a>
                    )}
                    {entry.facebook && (
                      <a
                        href={entry.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700"
                      >
                        <Facebook className="h-4 w-4" />
                      </a>
                    )}
                    {entry.cumpleanos && (
                      <span className="inline-flex items-center gap-1 text-slate-500">
                        <Cake className="h-4 w-4" />
                        <span className="hidden md:inline">{entry.cumpleanos}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer note */}
        <div className="mt-8 bg-zuno-wine/5 border border-zuno-wine/20 rounded-xl p-6 text-center">
          <p className="text-slate-700">
            ¿Quieres modificar tus datos o agregar información?
          </p>
          <p className="text-slate-600 mt-1">
            Envíanos un mensaje en la sección de{" "}
            <Link to="/#contacto" className="text-zuno-wine font-medium hover:underline">
              Contáctanos
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
